import { useContext, useState } from "react";
import {
  Button,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { CorePropsInterfaces, CoreInterfaces, DTOs } from "src/core/Models";
import * as Constants from "../../../core/Constants";
import { t } from "i18next";
import { AppContext } from "src/contexts/AppContext";
import {
  areAllFieldsCompleted,
  createEmptySignee,
  findQuestionInService,
  isFinalorOutdatedVersion,
  isSigneeDetailsDataFilled,
} from "src/utils";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlinedBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CircleRoundedIcon from "@mui/icons-material/CircleRounded";
import GtDialog from "src/components/Common/GtDialog";

function QuestionSigneeDetails({
  question,
  isDisabled,
  onChange,
}: CorePropsInterfaces.QuestionInputProps) {
  const { globalState, dispatch } = useContext(AppContext);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [contactPersonToDeleteUuid, setContactPersonToDeleteUuid] =
    useState<string>();
  const lastVersionChangedDate =
    globalState.currentConfiguration.lastModifiedDate;
  const isFinalOrOutdatedVersion = isFinalorOutdatedVersion(globalState);
  const isAllDataFilled = areAllFieldsCompleted(question);

  const isSigneeDetailsQuestion =
    question.data.inputType === Constants.QuestionInputType.SigneeDetails;
  let isSigneeDetailsQuestionFilled = false;
  if (isSigneeDetailsQuestion) {
    isSigneeDetailsQuestionFilled = isSigneeDetailsDataFilled(question);
  }
  const isDataIncomplete =
    isSigneeDetailsQuestion && !isSigneeDetailsQuestionFilled;

  function onFilledStateChange(isFilled: boolean) {
    if (isDisabled) {
      return;
    }
    const newQuestionDTO: DTOs.QuestionDTO = {
      ...question,
      state: {
        ...question.state,
        isFilled:
          question.data.userValue && isDataIncomplete ? false : isFilled,
      },
    };
    onChange && onChange(newQuestionDTO);
  }

  const userDetails =
    Array.isArray(question.data.userValue) && question.data.userValue.length > 0
      ? (question.data.userValue as CoreInterfaces.ClientSigneeDetails[])
      : [createEmptySignee()];

  const isDigitalSigning =
    findQuestionInService(
      globalState.services.find(
        (service) =>
          service.data.code === Constants.ServiceCode.GeneralInformation
      ),
      Constants.GeneralInformationQuestion.Q0017
    )?.data.userValue === Constants.YesNo.Yes;

  const addSignee = () => {
    if (userDetails.length < 10 && !isFinalOrOutdatedVersion) {
      dispatch({
        type: Constants.AppStateActions.AddSigneeDetails,
        payload: {
          questionCode: question.data.code,
        },
      });
    }
  };

  const deleteSignee = () => {
    dispatch({
      type: Constants.AppStateActions.DeleteSigneeDetails,
      payload: {
        contactPersonToDeleteUuid,
        questionCode: question.data.code,
      },
    });
  };

  const updateSignee = ({
    clientSigneeUuid,
    signeeIndex,
    property,
    value,
  }: {
    clientSigneeUuid: string;
    signeeIndex?: number;
    property: keyof CoreInterfaces.ClientSigneeDetails;
    value: string;
  }): void => {
    if (!isFinalOrOutdatedVersion) {
      dispatch({
        type: Constants.AppStateActions.UpdateSigneeDetails,
        payload: {
          questionCode: question.data.code,
          clientSigneeUuid,
          property,
          value,
          signeeIndex,
        },
      });
    }
  };

  const deleteDialogDTO: DTOs.GtDialogDTO = {
    data: {
      title: t("General.ClientDetails.DeleteSignee"),
      maxWidth: "xs",
      leftButtons: [],
      rightButtons: [
        <Button
          onClick={() => setIsModalVisible(!isModalVisible)}
          color="inherit"
          variant="contained"
          key="cancelAction"
        >
          {t("General.ClientDetails.Cancel")}
        </Button>,
        <Button
          onClick={() => {
            deleteSignee();
            setIsModalVisible(!isModalVisible);
          }}
          color="tertiary"
          variant="contained"
          key="delecteLicense"
        >
          {t("General.ClientDetails.DeleteSignee")}
        </Button>,
      ],
    },
    api: {
      onClose: () => setIsModalVisible(!isModalVisible),
    },
    state: {
      isOpen: isModalVisible,
      isFullWidth: true,
    },
  };

  return (
    <article
      className={`gt-questionRow gt-questionRow--clientDetails
           ${
             !question.data.userValue ||
             question.data.userValue ===
               Constants.HelpfulConstants.PleaseSelect ||
             isDataIncomplete
               ? "gt-questionRow--isNotAnsweredState"
               : ""
           } 
              ${
                question.state.isFilled && isAllDataFilled
                  ? "gt-questionRow--filledAnswerState"
                  : ""
              }
              ${
                !question.state.isFilled && isAllDataFilled
                  ? "gt-questionRow--unfilledAnswerState"
                  : ""
              }`}
    >
      {!isFinalOrOutdatedVersion && isModalVisible && (
        <GtDialog gtDialogDTO={deleteDialogDTO}>
          <article> {t("General.ClientDetails.DeleteSigneeQuestion")}</article>
        </GtDialog>
      )}
      <div className="gt-signeeDetails">
        <label className="gt-signeeDetails__title">
          {t(`Questions.${question.data.code}.Text`)}
        </label>
        {Array.isArray(userDetails) &&
          userDetails.map((signee, signeeIndex) => (
            <>
              <div key={signeeIndex} className="gt-signeeDetails__row">
                <TextField
                  key={signeeIndex + signee.Uuid}
                  data-testid={question.data.code + "FirstName"}
                  value={signee.FirstName ?? ""}
                  className="gt-TextField"
                  onChange={(event: any) =>
                    updateSignee({
                      clientSigneeUuid: signee.Uuid,
                      signeeIndex,
                      property: "FirstName",
                      value: event.target.value,
                    })
                  }
                  placeholder={t("General.ClientDetails.ClientSigneeFirstName")}
                  disabled={isFinalOrOutdatedVersion}
                />

                <TextField
                  key={signeeIndex + signee.Uuid + "LastName"}
                  data-testid={question.data.code + "LastName"}
                  value={signee.LastName ?? ""}
                  className="gt-TextField"
                  onChange={(event: any) =>
                    updateSignee({
                      clientSigneeUuid: signee.Uuid,
                      signeeIndex,
                      property: "LastName",
                      value: event.target.value,
                    })
                  }
                  placeholder={t("General.ClientDetails.ClientSigneeLastName")}
                  disabled={isFinalOrOutdatedVersion}
                />

                {isDigitalSigning && (
                  <>
                    <TextField
                      key={signeeIndex + signee.Uuid + "Email"}
                      error={
                        !!signee.Email &&
                        !Constants.EmailRegex.test(signee.Email)
                      }
                      id="outlined-error"
                      value={signee.Email ?? ""}
                      className="gt-TextField"
                      onChange={(event: any) =>
                        updateSignee({
                          clientSigneeUuid: signee.Uuid,
                          signeeIndex,
                          property: "Email",
                          value: event.target.value,
                        })
                      }
                      placeholder={t("General.ClientDetails.EmailSignee")}
                      disabled={isFinalOrOutdatedVersion}
                    />
                    <Tooltip
                      className="gt-questionRow__title-tooltip"
                      data-testid={question.data.code + "Tooltip"}
                      title={
                        <span
                          className="gt-tootipHint"
                          data-testid={question.data.code + "TooltipHint"}
                          dangerouslySetInnerHTML={{
                            __html: t(
                              "General.ClientDetails.DigitalSigningMethodServiceAgreementTooltip"
                            ),
                          }}
                        ></span>
                      }
                    >
                      <IconButton>
                        <InfoOutlinedIcon />
                      </IconButton>
                    </Tooltip>
                    <Select
                      key={signeeIndex + signee.Uuid}
                      data-testid={question.data.code}
                      className="gt-Select"
                      value={
                        (signee.SigningMethod as string) ??
                        Constants.SigningMethod.BankIDSE
                      }
                      onChange={(event: any) =>
                        updateSignee({
                          clientSigneeUuid: signee.Uuid,
                          signeeIndex,
                          property: "SigningMethod",
                          value: event.target.value,
                        })
                      }
                      disabled={
                        question.data.inputConstraints.nonEditableAnswer ||
                        isDisabled
                      }
                    >
                      {Object.values(Constants.SigningMethod).map(
                        (statusItem) => {
                          const translationKey =
                            Constants.SigningMethodTranslationMap[
                              statusItem as keyof typeof Constants.SigningMethodTranslationMap
                            ] || statusItem;
                          return (
                            <MenuItem key={statusItem} value={statusItem}>
                              {t(`Options.SigningMethod.${translationKey}`)}
                            </MenuItem>
                          );
                        }
                      )}
                    </Select>
                  </>
                )}
                {userDetails.length > 1 ? (
                  <Tooltip
                    title={t(`General.ClientDetails.RemoveTooltip`)}
                    placement="right"
                  >
                    <Button
                      className="gt-clientDetails__delete-signee"
                      onClick={() => {
                        setContactPersonToDeleteUuid(signee.Uuid);
                        setIsModalVisible(true);
                      }}
                      disabled={isFinalOrOutdatedVersion}
                    >
                      <PersonRemoveIcon />
                    </Button>
                  </Tooltip>
                ) : (
                  <section className="gt-clientDetails__extraSpace"></section>
                )}
              </div>
              {signeeIndex === userDetails.length - 1 && (
                <Tooltip title={t("General.ClientDetails.AddSigneeTextButton")}>
                  <Button
                    className={`gt-clientDetails__add-signee 
                        ${
                          isFinalOrOutdatedVersion || userDetails.length > 9
                            ? "gt-clientDetails__add-signee--disabled"
                            : ""
                        }`}
                    onClick={() => addSignee()}
                    disabled={isFinalOrOutdatedVersion}
                  >
                    <PersonAddAlt1Icon />
                  </Button>
                </Tooltip>
              )}
              <div className="gt-signeeDetails__row-icons">
                <section className="gt-questionRow__comment"></section>
                <section className="gt-questionRow__comment"></section>

                {signeeIndex === userDetails.length - 1 && (
                  <>
                    <section className="gt-questionRow__indicators">
                      <span className="gt-questionRow__indicators-icon">
                        <CircleRoundedIcon fontSize="small" />
                      </span>
                      <span className="gt-questionRow__indicators-iconFill">
                        {question.state.isFilled && isAllDataFilled ? (
                          <CheckBoxIcon
                            fontSize="small"
                            onClick={() =>
                              onFilledStateChange(!question.state.isFilled)
                            }
                          />
                        ) : (
                          <CheckBoxOutlinedBlankIcon
                            fontSize="small"
                            onClick={() =>
                              onFilledStateChange(!question.state.isFilled)
                            }
                          />
                        )}
                      </span>
                    </section>
                    <section className="gt-questionRow__comment"></section>
                  </>
                )}
              </div>
            </>
          ))}
      </div>
    </article>
  );
}

export default QuestionSigneeDetails;
